import React, { useRef, useEffect, useState, useCallback } from 'react';
import Icon from '../../../components/AppIcon';

const SignaturePad = ({ onSignatureChange, required = true }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const getPos = (e, canvas) => {
    const rect = canvas?.getBoundingClientRect();
    const clientX = e?.touches?.[0]?.clientX ?? e?.clientX;
    const clientY = e?.touches?.[0]?.clientY ?? e?.clientY;
    return {
      x: (clientX - rect?.left) * (canvas?.width / rect?.width),
      y: (clientY - rect?.top) * (canvas?.height / rect?.height)
    };
  };

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const container = canvas?.parentElement;
    const w = container?.clientWidth || 500;
    const h = 160;
    canvas.width = w * window.devicePixelRatio;
    canvas.height = h * window.devicePixelRatio;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas?.getContext('2d');
    ctx?.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setCanvasSize({ width: w, height: h });
  }, []);

  useEffect(() => {
    setupCanvas();
    const observer = new ResizeObserver(setupCanvas);
    if (canvasRef?.current?.parentElement) {
      observer?.observe(canvasRef?.current?.parentElement);
    }
    return () => observer?.disconnect();
  }, [setupCanvas]);

  const startDrawing = (e) => {
    e?.preventDefault();
    const canvas = canvasRef?.current;
    if (!canvas) return;
    isDrawingRef.current = true;
    lastPosRef.current = getPos(e, canvas);
  };

  const draw = (e) => {
    e?.preventDefault();
    if (!isDrawingRef?.current) return;
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    const pos = getPos(e, canvas);
    ctx?.beginPath();
    ctx?.moveTo(lastPosRef?.current?.x, lastPosRef?.current?.y);
    ctx?.lineTo(pos?.x, pos?.y);
    ctx?.stroke();
    lastPosRef.current = pos;
    if (!hasSignature) {
      setHasSignature(true);
      onSignatureChange?.(canvas?.toDataURL('image/png'));
    } else {
      onSignatureChange?.(canvas?.toDataURL('image/png'));
    }
  };

  const stopDrawing = (e) => {
    e?.preventDefault();
    isDrawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
    onSignatureChange?.(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="PenLine" size={16} color="var(--color-primary)" />
          <span className="text-sm font-semibold text-gray-900">
            Digital Signature
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </div>
        {hasSignature && (
          <button
            type="button"
            onClick={clearSignature}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-500 transition-colors px-2 py-1 rounded-md hover:bg-red-50"
          >
            <Icon name="Eraser" size={13} />
            Clear
          </button>
        )}
      </div>

      <div className={`relative rounded-xl border-2 transition-colors overflow-hidden ${
        hasSignature ? 'border-primary/40 bg-white' : 'border-dashed border-gray-300 bg-gray-50'
      }`}>
        <canvas
          ref={canvasRef}
          className="block w-full cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <Icon name="PenLine" size={24} color="#9ca3af" />
            <p className="text-sm text-gray-400 mt-2">Sign here</p>
            <p className="text-xs text-gray-400">Draw your signature above</p>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 flex items-center gap-1.5">
        <Icon name="Shield" size={12} color="#6b7280" />
        Your signature is legally binding and will be recorded with a timestamp.
      </p>
    </div>
  );
};

export default SignaturePad;
